/**
 * Fullscreen wrapper that embeds app.binyumatv.com auth (signin/signup) in an iframe.
 * Used on mobile so that when the user clicks Sign In or Sign Up from the website
 * (including when opened from the website PWA), they see the app auth UI filling
 * the screen with no browser chrome—instead of opening app.binyumatv.com in a
 * new tab where the URL bar/header is visible.
 *
 * Requires app.binyumatv.com to allow framing from this origin (e.g. CSP
 * frame-ancestors https://binyumatv.com https://www.binyumatv.com) on the
 * app's hosting (e.g. CloudFront response headers).
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

const APP_ORIGIN = 'https://app.binyumatv.com'

export function AppAuth() {
  const location = useLocation()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const isSignup = location.pathname === '/app/signup'
  const path = isSignup ? '/auth/signup' : '/auth/signin'
  const src = `${APP_ORIGIN}${path}?embedded=1`

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== APP_ORIGIN) return
      const data = event.data
      if (data?.type === 'binyumatv-auth-done') {
        // Optional: redirect to the app so user continues there
        window.location.href = APP_ORIGIN + (data.path || '/')
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      <iframe
        ref={iframeRef}
        src={src}
        title={isSignup ? 'Create account' : 'Sign in'}
        className="flex-1 w-full min-h-0 border-0"
        allow="fullscreen"
        allowFullScreen
      />
    </div>
  )
}
